/**
 * Image Preloader Utility
 * Preloads images and tracks progress for smooth user experience
 */

import { Config } from '../types/config.types';

export interface PreloadProgress {
  loaded: number;
  total: number;
  percentage: number;
  currentImage?: string;
}

const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

const extractImagePaths = (config: Config): string[] => {
  const images: string[] = [];

  // Business logo
  if (config.business.logo) {
    images.push(config.business.logo);
  }

  // Hero images (always enabled if hero is enabled)
  if (config.components.hero.enabled) {
    if (config.components.hero.slideshow.enabled && config.components.hero.slideshow.images.length > 0) {
      images.push(...config.components.hero.slideshow.images);
    } else if (config.components.hero.backgroundImage) {
      images.push(config.components.hero.backgroundImage);
    }
  }

  // About section (only if enabled)
  if (config.components.about.enabled) {
    if (config.components.about.teamPhoto) {
      images.push(config.components.about.teamPhoto);
    }
    
    if (config.components.about.teamGallery?.enabled && config.components.about.teamGallery.images.length > 0) {
      images.push(...config.components.about.teamGallery.images);
    }

    if (config.components.about.beforeAfterSlideshow?.enabled && config.components.about.beforeAfterSlideshow.slides.length > 0) {
      config.components.about.beforeAfterSlideshow.slides.forEach(slide => {
        if (slide.before) images.push(slide.before);
        if (slide.after) images.push(slide.after);
      });
    }
  }

  // Services (only if enabled)
  if (config.components.services.enabled && config.components.services.items.length > 0) {
    config.components.services.items.forEach(service => {
      if (service.image) {
        images.push(service.image);
      }
      if (service.gallery?.enabled && service.gallery.images.length > 0) {
        images.push(...service.gallery.images);
      }
    });
  }

  // Portfolio (only if enabled)
  if (config.components.portfolio.enabled && config.components.portfolio.items.length > 0) {
    config.components.portfolio.items.forEach(item => {
      if (item.image) {
        images.push(item.image);
      }
      if (item.gallery && item.gallery.length > 0) {
        images.push(...item.gallery);
      }
      if (item.beforeAfter?.enabled) {
        if (item.beforeAfter.before) images.push(item.beforeAfter.before);
        if (item.beforeAfter.after) images.push(item.beforeAfter.after);
      }
    });
  }

  // Testimonials (only if enabled) - Skip photos as they're not used
  if (config.components.testimonials.enabled && config.components.testimonials.items.length > 0) {
    config.components.testimonials.items.forEach(testimonial => {
      // Only preload photo if it exists and is not the placeholder
      if (testimonial.photo && testimonial.photo !== '/assets/images/testimonial-avatar.jpg') {
        images.push(testimonial.photo);
      }
    });
  }

  // Blog (only if enabled)
  if (config.components.blog.enabled && config.components.blog.items.length > 0) {
    config.components.blog.items.forEach(post => {
      if (post.image) {
        images.push(post.image);
      }
    });
  }

  // CTA Blocks (only if enabled)
  if (config.components.ctaBlocks.enabled && config.components.ctaBlocks.items.length > 0) {
    config.components.ctaBlocks.items.forEach(cta => {
      if (cta.backgroundImage) {
        images.push(cta.backgroundImage);
      }
    });
  }

  // Remove duplicates and empty strings
  return [...new Set(images)].filter(img => img && img.trim() !== '');
};

export const preloadConfigImages = async (
  config: Config,
  onProgress?: (progress: PreloadProgress) => void
): Promise<void> => {
  const imagePaths = extractImagePaths(config);
  const total = imagePaths.length;
  let loaded = 0;

  if (total === 0) {
    if (onProgress) {
      onProgress({ loaded: 0, total: 0, percentage: 100 });
    }
    return;
  }

  const promises = imagePaths.map(async (src) => {
    try {
      await preloadImage(src);
      loaded++;
      if (onProgress) {
        onProgress({
          loaded,
          total,
          percentage: Math.round((loaded / total) * 100),
          currentImage: src,
        });
      }
    } catch (error) {
      // Log error but don't fail the entire preload
      console.warn(`Failed to preload image: ${src}`, error);
      loaded++;
      if (onProgress) {
        onProgress({
          loaded,
          total,
          percentage: Math.round((loaded / total) * 100),
          currentImage: src,
        });
      }
    }
  });

  await Promise.all(promises);
};